<template>
  <div class="country-co" :class="$x.nsAddressForm">
    <VTextField
      v-model="address.address1"
      name="address1"
      maxlength="40"
      required
      validate-on-blur
      :label="t.address1Label"
      class="flex-column"
      :rules="address1Rules"
      @input="addressUpdated"
      @blur="onBlur"
    ></VTextField>
    <VTextField
      v-model="address.address2"
      name="address2"
      maxlength="34"
      validate-on-blur
      class="flex-column"
      :aria-label="t.address2Label"
      :rules="address2Rules"
      @input="addressUpdated"
      @blur="onBlur"
    >
      <template #label>
        {{ t.address2Label }}
        <span :class="$x.optional"> {{ t.optional }} </span>
      </template>
    </VTextField>
    <div :class="$x.dualColumn">
      <NsSelect
        ref="regionSelect"
        v-model="address.region"
        :items="regions"
        name="region"
        :label="t.regionLabel"
        :rules="stateRules"
        :class="$x.firstCol"
        @input="addressUpdated"
        @blur="onBlur"
      ></NsSelect>
      <NsSelect
        ref="citySelect"
        v-model="address.city"
        :items="citiesForCurrentRegion"
        name="city"
        required
        :disabled="cityDisabled"
        validate-on-blur
        :label="t.cityLabel"
        :class="$x.firstCol"
        :rules="cityRules"
        @input="addressUpdated"
        @blur="onBlur"
      ></NsSelect>
    </div>
    <VTextField
      v-if="includeEmail"
      v-model="address.email"
      name="email"
      maxlength="50"
      required
      validate-on-blur
      :label="t.emailLabel"
      class="flex-column"
      :rules="emailRules"
      @input="addressUpdated"
      @blur="onBlur"
    ></VTextField>
  </div>
</template>

<script>
import { VTextField } from 'vuetify/lib';
import NsSelect from '../../NsSelect.vue';
import ADDRESS_MIXIN from '../addressMixin';
import STATIC_RESOURCE_MIXIN from '../staticResourceMixin';
import REGIONS from './regions.json';
import CITIES from './region-cities.json';

// Most of the logic for this component is in the mixin
import TRANSLATIONS from './translations';

function titleCase(str) {
  str = str.toLowerCase().split(' ');
  for (var i = 0; i < str.length; i++) {
    str[i] = str[i].charAt(0).toUpperCase() + str[i].slice(1);
  }
  return str.join(' ');
}

export default {
  name: 'NsAddressCo',
  components: {
    VTextField,
    NsSelect,
  },
  mixins: [ADDRESS_MIXIN, STATIC_RESOURCE_MIXIN],
  data() {
    return {
      regions: [],
      regionCities: {},
    };
  },
  computed: {
    citiesForCurrentRegion() {
      let cities = [];
      const regionValue = this.address.region;
      if (regionValue) {
        const currentRegionCities = this.regionCities[regionValue];
        /* istanbul ignore else */
        cities = currentRegionCities ? currentRegionCities.cities : [];
      }
      return cities;
    },
    cityDisabled() {
      const cities = this.citiesForCurrentRegion;
      /* istanbul ignore next */
      return cities.length > 0 ? null : 'disabled';
    },
  },
  created() {
    this.loadCOResources();
  },
  mounted() {
    this.address.country = 'CO';
    if (TRANSLATIONS[this.lang]) {
      this.t = TRANSLATIONS[this.lang];
    } else {
      // If the language isn't available then get the first language in translations file
      this.t = TRANSLATIONS[Object.keys(TRANSLATIONS)[0]];
    }
    this.$emit('new-address-in-form');
  },
  methods: {
    async loadCOResources() {
      try {
        const results = await this.loadStaticResources([
          {
            key: 'regions',
            path: 'CO/regions.json',
            contentType: 'application/json',
            defaultData: REGIONS,
          },
          {
            key: 'regionCities',
            path: 'CO/region-cities.json',
            contentType: 'application/json',
            defaultData: CITIES,
          },
        ]);
        const loadedRegions = results.regions;
        loadedRegions.forEach((region) => this.regions.push(region));
        const loadedCities = results.regionCities;
        for (const regionKey in loadedCities) {
          const region = loadedCities[regionKey];
          region.cities = region.cities.map((city) => titleCase(city));
          this.regionCities[regionKey] = region;
        }
      } catch (err) {
        /* istanbul ignore next */
        console.log(err);
      }
    },
  },
};
</script>

<style lang="scss" module="$x">
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/colors";
@use "~@nuskin/ns-core-styles/src/scss/typographyVariables";

$color-eeyore: map.get(colors.$baseColors, eyore);

::v-deep *:not(.v-icon) {
  font-family: map.get(typographyVariables.$typography, font-family-primary);
}

.nsAddressForm {
  .dualColumn {
    display: flex;
    align-content: space-between;
    .firstCol {
      margin-right: 20px;
    }
  }
}

.optional {
  color: $color-eeyore;
}
</style>
